import { Order } from "@dining/api-client";
import { CardFormValues } from "../../sharedTypes";
import { AppProps, StateSlice } from "./app";
import { trackEvent } from "../../appInsights";
import { appInsightsEvents } from "../../constants";
import { createCardPayment } from "../../payments.utils";

export type OrdersProps = {
  orders: {
    values?: Order[];
    loading?: boolean;
    error?: Error;
    selected?: Order;
  };
};

export type OrdersState = OrdersProps & {
  orders: {
    fetch: () => Promise<void>;
    submit: (values: CardFormValues) => Promise<void>;
    setLoading: (loading: boolean) => void;
    setValues: (values?: Order[]) => void;
    setSelected: (selected?: Order) => void;
    setError: (error?: Error) => void;
  };
};

export const defaultOrdersProps: OrdersProps = {
  orders: {
    loading: false,
  },
};

export const createOrdersState: (props?: Partial<AppProps>) => StateSlice<OrdersState> =
  (props) => (set, get) => ({
    orders: {
      ...defaultOrdersProps.orders,
      ...props?.orders,
      setLoading: (loading: boolean) =>
        set(
          (state) => ({
            ...state,
            orders: {
              ...state.orders,
              loading,
            },
          }),
          false,
          {
            type: "orders.setLoading",
            loading,
          },
        ),
      setValues: (values?: Order[]) =>
        set(
          (state) => ({
            ...state,
            orders: {
              ...state.orders,
              values,
            },
          }),
          false,
          {
            type: "orders.setValues",
            values,
          },
        ),
      setSelected: (selected?: Order) =>
        set(
          (state) => ({
            ...state,
            orders: {
              ...state.orders,
              selected,
            },
          }),
          false,
          {
            type: "orders.setSelected",
            selected,
          },
        ),
      setError: (error?: Error) =>
        set(
          (state) => ({
            ...state,
            orders: {
              ...state.orders,
              error,
            },
          }),
          false,
          {
            type: "orders.setError",
            error,
          },
        ),
      fetch: async () => {
        const {
          client,
          orders: { setValues, setError },
        } = get();
        try {
          if (client) {
            const response = await client.getActiveOrdersList();
            setValues(response.items!);
          } else {
            setError(new Error("client is not defined"));
          }
        } catch (error) {
          setError(error as Error);
        }
      },
      submit: async (data: CardFormValues) => {
        const {
          appInsights,
          client,
          context,
          orders: { setLoading, fetch, setSelected, setError },
        } = get();
        try {
          setLoading(true);
          const payment = createCardPayment(data, context);
          const order = await client?.payWithCard(undefined, payment);
          if (order) {
            fetch();
            trackEvent(appInsights, appInsightsEvents.Usage.Actions.OrderPlaced, context, {
              paymentType: "Card",
              amount: order.totalAmount,
              orderId: order.id,
            });
            setSelected(order);
          }
        } catch (error) {
          setError(error as Error);
        } finally {
          setLoading(false);
        }
      },
    },
  });
